/* MatchingReportForm.jsx */
import React, { useState, useEffect, useMemo } from 'react';
import { Button, TextField, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ProfitAndLossService from '../../../Services/profitAndLoss.service';
import EntryRow from './EntryRow';
import AddEntryForm from './AddEntryForm';
import Constants from '../../../assests/Constants/constants';

dayjs.extend(utc);

const MatchingReportForm = ({
  // Data
  addItems = [],
  subtractItems = [],
  taxLiabilityItems = [],
  totalProfit = 0,
  taxableIncomeBase = 0,
  taxableIncomeRateItem = null,
  taxableIncomePercentage = '',
  taxPercentage = 0,
  currentTaxExpenses = 0,
  deferredTaxExpenses = 0,
  totalTaxExpenses = 0,
  matchingReports = [],
  sortCodeOptions = [],
  year = null,
  client = null,
  loading = false,
  
  // State
  addingToAdd = false,
  addingToSubtract = false,
  addingInSummary = null,
  
  // Form state
  keyType = 'sortcode',
  selectedSortCode = '',
  manualKey = '',
  value = '',
  isAmountPercentage = true,
  
  // Handlers
  setAddingInSummary = () => {},
  setAddingToAdd = () => {},
  setAddingToSubtract = () => {},
  setKeyType = () => {},
  setSelectedSortCode = () => {},
  setManualKey = () => {},
  setValue = () => {},
  setIsAmountPercentage = () => {},
  handleAddEntry = () => {},
  handleCancelAdd = () => {},
  handleEdit = () => {},
  handleDelete = () => {},
  handleDownloadPdf = () => {},
  getKeyDisplay = () => {},
  renderInlineForm = () => null,
  setTaxableIncomePercentage = () => {},
  setDeferredTaxExpenses = () => {},
  setMatchingReports = () => {},
  handleSaveTaxableIncomePercentage = null,
  
  // Styles
  inputPropsStyle = {},
  
  // Translation
  t = (s) => s,
  
  // New props for percentage calculation
  onGetSortCodeTotal = null,
  token = ''
}) => {

  console.log('totalProfit', totalProfit);
  console.log('taxableIncomeBase', taxableIncomeBase);
  console.log('taxLiabilityItems', taxLiabilityItems);
  
  // Track the original taxable income percentage value
  const [originalTaxableIncomePercentage, setOriginalTaxableIncomePercentage] = useState('');
  const [currentTaxableIncomePercentageInput, setCurrentTaxableIncomePercentageInput] = useState('');
  
  // Track the original deferred tax expenses value
  const [originalDeferredTaxExpenses, setOriginalDeferredTaxExpenses] = useState(0);
  const [currentDeferredTaxExpensesInput, setCurrentDeferredTaxExpensesInput] = useState(0);
  
  // Normalize year: extract year from Date object if needed, otherwise use as-is
  const normalizedYear = useMemo(() => {
    if (!year) return null;
    // If year is a Date object, extract the year using UTC
    if (year instanceof Date) {
      return dayjs.utc(year).year();
    }
    // If year is already a number, use it directly
    if (typeof year === 'number') {
      return year;
    }
    // If year is a string that can be parsed, try to extract year
    if (typeof year === 'string') {
      const parsed = dayjs.utc(year);
      if (parsed.isValid()) {
        return parsed.year();
      }
    }
    return year;
  }, [year]);
  console.log('normalizedYear', normalizedYear);
  // Initialize original value when taxableIncomeRateItem or taxableIncomePercentage changes
  useEffect(() => {
    const originalValue = taxableIncomeRateItem ? taxableIncomeRateItem.value : taxableIncomePercentage;
    setOriginalTaxableIncomePercentage(originalValue || '');
    setCurrentTaxableIncomePercentageInput(originalValue || '');
  }, [taxableIncomeRateItem?.id, taxableIncomePercentage]);
  
  // Get the deferred tax item from database (not temp entries)
  const deferredTaxItemFromDB = matchingReports.find(r => r.key === 'הוצאות מס נדחות' && r.type === 2 && !r.id.toString().startsWith('temp_'));
  
  // Initialize original deferred tax expenses value - only when the item from database changes
  useEffect(() => {
    const originalValue = deferredTaxItemFromDB ? (parseFloat(deferredTaxItemFromDB.value) || 0) : deferredTaxExpenses;
    setOriginalDeferredTaxExpenses(originalValue);
    setCurrentDeferredTaxExpensesInput(originalValue);
  }, [deferredTaxItemFromDB?.id]);
  
  // Check if the value has changed
  const hasTaxableIncomePercentageChanged = currentTaxableIncomePercentageInput !== originalTaxableIncomePercentage;
  const hasDeferredTaxExpensesChanged = currentDeferredTaxExpensesInput !== originalDeferredTaxExpenses;
  
  // Check if client has "חד צידית" AccountManagement
  const isSingleSidedAccountManagement = client?.accountManagement === Constants.AccountManagementOptions[1].value || 
                                        client?.accountManagement === Constants.AccountManagementOptions[1].name;
  
  // Calculate total tax expenses using current input value for deferred tax expenses (for immediate display)
  const calculatedTotalTaxExpenses = currentTaxExpenses + currentDeferredTaxExpensesInput;
  // Handle save for taxable income percentage
  const handleSaveTaxableIncomePercentageLocal = async () => {
    const percentage = currentTaxableIncomePercentageInput;
    
    // If handler is provided, use it to save immediately to database
    if (handleSaveTaxableIncomePercentage) {
      await handleSaveTaxableIncomePercentage(percentage);
      // Update original value after successful save
      setOriginalTaxableIncomePercentage(percentage);
    } else {
      // Fallback: just update local state (for backward compatibility)
      setTaxableIncomePercentage(percentage);
      
      // Update or create the special entry
      const existingItem = matchingReports.find(r => r.key === 'הכנסה חייבת בשיעור' && r.type === 1);
      if (existingItem) {
        setMatchingReports(prev => prev.map(r => 
          r.id === existingItem.id 
            ? { ...r, value: percentage, isAmountPercentage: false, isModified: true }
            : r
        ));
      } else if (percentage || percentage === '0') {
        // Allow creating even if 0
        const newItem = {
          id: `temp_taxable_income_rate_${Date.now()}`,
          caseId: client?.caseId,
          key: 'הכנסה חייבת בשיעור',
          isInternalKey: false,
          internalKey: null,
          type: 1, // Subtract
          value: percentage,
          isAmountPercentage: false, // This is a percentage
          year: normalizedYear,
          isNew: true
        };
        setMatchingReports(prev => [...prev, newItem]);
      }
      
      // Update original value after save
      setOriginalTaxableIncomePercentage(percentage);
    }
  };
  
  // Handle save for deferred tax expenses
  const handleSaveDeferredTaxExpensesLocal = async () => {
    const value = currentDeferredTaxExpensesInput;
    
    if (!token) {
      toast.error(t('TokenRequired') || 'Authentication token is required.');
      return;
    }
    
    try {
      const existingItem = matchingReports.find(r => r.key === 'הוצאות מס נדחות' && r.type === 2);
      
      if (existingItem) {
        // Update existing entry
        const reportToSave = {
          id: existingItem.id.toString().startsWith('temp_') 
            ? '00000000-0000-0000-0000-000000000000' 
            : existingItem.id,
          caseId: existingItem.caseId || client?.caseId,
          key: 'הוצאות מס נדחות',
          isInternalKey: false,
          internalKey: null,
          type: 2, // TaxLiability
          value: value.toString(),
          isAmountPercentage: true,
          year: existingItem.year || normalizedYear,
          isFromProfitAndLossDetailed: existingItem.isFromProfitAndLossDetailed ?? false
        };
        
        const savedReports = await ProfitAndLossService.SaveMatchingProfitAndLossReports([reportToSave], token);
        
        if (savedReports && savedReports.length > 0) {
          // Update local state with saved report
          setMatchingReports(prev => prev.map(r => 
            r.id === existingItem.id 
              ? { ...savedReports[0], isNew: false, isModified: false }
              : r
          ));
          
          // Update the deferred tax expenses state
          setDeferredTaxExpenses(value);
          setOriginalDeferredTaxExpenses(value);
          
          toast.success(t('DeferredTaxExpensesSaved') || 'Deferred tax expenses saved successfully.');
        }
      } else {
        // Create new entry
        const newReport = {
          id: '00000000-0000-0000-0000-000000000000',
          caseId: client?.caseId,
          key: 'הוצאות מס נדחות',
          isInternalKey: false,
          internalKey: null,
          type: 2, // TaxLiability
          value: value.toString(),
          isAmountPercentage: true,
          year: normalizedYear,
          isFromProfitAndLossDetailed: false
        };
        
        const savedReports = await ProfitAndLossService.SaveMatchingProfitAndLossReports([newReport], token);
        
        if (savedReports && savedReports.length > 0) {
          // Add saved report to local state
          setMatchingReports(prev => [...prev, { ...savedReports[0], isNew: false, isModified: false }]);
          
          // Update the deferred tax expenses state
          setDeferredTaxExpenses(value);
          setOriginalDeferredTaxExpenses(value);
          
          toast.success(t('DeferredTaxExpensesSaved') || 'Deferred tax expenses saved successfully.');
        }
      }
    } catch (error) {
      console.error('Error saving deferred tax expenses:', error);
      toast.error(t('ErrorSavingDeferredTaxExpenses') || `Error saving deferred tax expenses: ${error.message}`);
    }
  };
  
  return (
    <div style={{
      flex: '1 1 55%',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f3f4fe',
      border: '1px solid #E0E0E0',
      borderRadius: 8,
      padding: 16,
      overflow: 'auto',
    }}>


      {/* Summary Section - Matching Reports Preview */}
      <div style={{ 
        marginTop: 10, 
        padding: 16, 
        border: '1px solid #E0E0E0', 
        borderRadius: 8, 
        backgroundColor: '#f9f9f9',
        direction: 'rtl',
        textAlign: 'right',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>
            {t('TaxAdjustmentReportForIncomeTax')} {normalizedYear}
          </div>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDownloadPdf}
            disabled={loading}
            sx={{ 
              color: '#304FFF !important', 
              display: 'flex', 
              alignItems: 'center', 
              borderColor: '#304FFF !important',
              gap: 0.5, 
              fontSize: '12px', 
              fontWeight: '600',
              '&:hover': {
                backgroundColor: 'rgba(48, 79, 255, 0.08) !important',
                color: '#304FFF !important'
              }
            }}
            endIcon={<DownloadIcon />}
          >
            {t('DownloadMatchingReportPdf') || 'הורד דוח מותאם PDF'}
          </Button>
        </div>
        
        <div style={{ fontSize: '14px', marginBottom: 12, display: 'flex', direction: 'rtl', gap: 10 }}>
          <span style={{ textAlign: 'right', minWidth: '180px', flexShrink: 0 }}><strong>{t('ProfitBeforeTax')}:</strong></span>
          <span style={{ textAlign: 'left', width: '100px', flexShrink: 0 }}>
            {totalProfit < 0 
              ? `(${Math.abs(totalProfit).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
              : totalProfit.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            }
          </span>
        </div>

        {/* הוסף Items Summary */}
        <div style={{ marginTop: 10, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              <strong>{t('Add') || 'הוסף'}:</strong>
            </div>
            {addingInSummary !== 'add' && (
              <IconButton
                size="small"
                onClick={() => {
                  setAddingInSummary('add');
                  setAddingToAdd(false);
                  setAddingToSubtract(false);
                  setSelectedSortCode('');
                  setManualKey('');
                  setValue('');
                  setKeyType('sortcode');
                  setIsAmountPercentage(true);
                }}
                sx={{ color: '#304FFF' }}
              >
                <AddIcon />
              </IconButton>
            )}
          </div>
          
          {/* Inline form */}
          {addingInSummary === 'add' && renderInlineForm('add', 0)}
          
          {/* Items list */}
          {addItems.length > 0 && (
            <div>
              {addItems.map((item) => {
                const keyDisplay = getKeyDisplay(item);
                const value = parseFloat(item.value) || 0;
                return (
                  <div key={item.id} style={{ marginBottom: 5, fontSize: '14px', display: 'flex', direction: 'rtl', alignItems: 'center', gap: 5 }}>
                    <span style={{ textAlign: 'right', minWidth: '180px', flexShrink: 0 }}>{keyDisplay}:</span>
                    <span style={{ textAlign: 'left', width: '100px', flexShrink: 0 }}>
                      {value < 0 
                        ? `(${Math.abs(value).toLocaleString('he-IL', { maximumFractionDigits: 3 })})`
                        : value.toLocaleString('he-IL', { maximumFractionDigits: 3 })
                      }
                    </span>
                    <IconButton 
                      size="small" 
                      onClick={() => handleEdit(item)} 
                      sx={{ color: '#304FFF', padding: '2px' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(item.id)} 
                      sx={{ color: 'red', padding: '2px' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* הפחת Items Summary */}
        <div style={{ marginTop: 10, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              <strong>{t('Subtract') || 'הפחת'}:</strong>
            </div>
            {addingInSummary !== 'subtract' && (
              <IconButton
                size="small"
                onClick={() => {
                  setAddingInSummary('subtract');
                  setAddingToAdd(false);
                  setAddingToSubtract(false);
                  setSelectedSortCode('');
                  setManualKey('');
                  setValue('');
                  setKeyType('sortcode');
                  setIsAmountPercentage(true);
                }}
                sx={{ color: '#304FFF' }}
              >
                <AddIcon />
              </IconButton>
            )}
          </div>
          
          {/* Inline form */}
          {addingInSummary === 'subtract' && renderInlineForm('subtract', 1)}
          
          {/* Items list */}
          {subtractItems.filter(item => item.key !== 'הכנסה חייבת בשיעור').length > 0 && (
            <div>
              {subtractItems.filter(item => item.key !== 'הכנסה חייבת בשיעור').map((item) => {
                const keyDisplay = getKeyDisplay(item);
                const value = parseFloat(item.value) || 0;
                return (
                  <div key={item.id} style={{ marginBottom: 5, fontSize: '14px', display: 'flex', direction: 'rtl', alignItems: 'center', gap: 5 }}>
                    <span style={{ textAlign: 'right', minWidth: '180px', flexShrink: 0 }}>{keyDisplay}:</span>
                    <span style={{ textAlign: 'left', width: '100px', flexShrink: 0 }}>({Math.round(value).toString()})</span>
                    <IconButton 
                      size="small" 
                      onClick={() => handleEdit(item)} 
                      sx={{ color: '#304FFF', padding: '2px' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(item.id)} 
                      sx={{ color: 'red', padding: '2px' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Special form for "הכנסה חייבת בשיעור" - Standalone section - Hide for "חד צידית" */}
        {!isSingleSidedAccountManagement && (
          <div style={{ marginTop: 10, marginBottom: 10 }}>
            {/* Special form for "הכנסה חייבת בשיעור" */}
            <div style={{ marginBottom: 5, fontSize: '14px', display: 'flex', direction: 'rtl', alignItems: 'center', gap: 10 }}>
              {/* Wrapper for label, input, and value with underline */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderBottom: '2px solid #000', paddingBottom: '3px', width: 'fit-content' }}>
                <span style={{ textAlign: 'right', flexShrink: 0 }}>{t('TaxableIncomeRate')}:</span>
                <TextField
                  size="small"
                  type="number"
                  placeholder="0"
                  value={currentTaxableIncomePercentageInput}
                  onChange={(e) => {
                    setCurrentTaxableIncomePercentageInput(e.target.value);
                  }}
                  style={{ width: '50px',}}
                  InputProps={{
                    style: { paddingLeft: 0, paddingRight:4, flexDirection: 'row-reverse'  },
                    endAdornment: <span style={{ marginLeft: 1, textAlign: 'center' }}>%</span>
                  }}
                  inputProps={{
                    style: { padding: 0, textAlign: 'center' }
                  }}
                />
                {/* Show CheckIcon when value has changed - outside the underlined area */}
                {hasTaxableIncomePercentageChanged && (
                  <IconButton
                    size="small"
                    onClick={handleSaveTaxableIncomePercentageLocal}
                    disabled={loading}
                    sx={{ color: '#304FFF', padding: '4px' }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                )}
                {taxableIncomeBase  && taxPercentage > 0 && (
                  <span style={{ textAlign: 'left', width: '100px', flexShrink: 0 }}>
                    {taxableIncomeBase.toLocaleString('he-IL', { maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* סה״כ הכנסה חייבת - Total Taxable Income - Always show */}
        {taxableIncomeBase  && (
          <div style={{ marginTop: 10, marginBottom: 10 }}>
            <div style={{ marginBottom: 5, fontSize: '14px', display: 'flex', direction: 'rtl', gap: 10 }}>
              <span style={{ textAlign: 'right', minWidth: '180px', flexShrink: 0 }}>{t('TotalTaxableIncome') || 'סה״כ הכנסה חייבת'}:</span>
              <span style={{ textAlign: 'left', width: '100px', flexShrink: 0 }}>
                {taxableIncomeBase < 0 
                  ? `(${Math.abs(taxableIncomeBase).toLocaleString('he-IL', { maximumFractionDigits: 2 })})`
                  : taxableIncomeBase.toLocaleString('he-IL', { maximumFractionDigits: 2 })
                }
              </span>
            </div>
          </div>
        )}

        {/* חבות מס Items Summary - Only show when taxPercentage > 0 and not "חד צידית" */}
        {taxPercentage > 0 && !isSingleSidedAccountManagement && (
          <div style={{ marginTop: 10, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                <strong>{t('TaxLiability') || 'חבות מס'}:</strong>
              </div>
              {addingInSummary !== 'taxLiability' && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setAddingInSummary('taxLiability');
                    setAddingToAdd(false);
                    setAddingToSubtract(false);
                    setSelectedSortCode('');
                    setManualKey('');
                    setValue('');
                    setKeyType('sortcode');
                    setIsAmountPercentage(true);
                  }}
                  sx={{ color: '#304FFF' }}
                >
                  <AddIcon />
                </IconButton>
              )}
            </div>
            
            {/* Inline form */}
            {addingInSummary === 'taxLiability' && renderInlineForm('taxLiability', 2)}
            
            {/* Items list */}
            {taxLiabilityItems.length > 0 && (
              <div>
                {taxLiabilityItems.map((item) => {
                  const keyDisplay = getKeyDisplay(item);
                  const value = parseFloat(item.value) || 0;
                  // Don't show delete icon for "בגין מס חברות" as it's auto-generated from percentage
                  const isAutoGenerated = item.key === 'בגין מס חברות';
                  return (
                    <div key={item.id} style={{ marginBottom: 5, fontSize: '14px', display: 'flex', direction: 'rtl', alignItems: 'center', gap: 10 }}>
                      <span style={{ textAlign: 'right', minWidth: '180px', flexShrink: 0 }}>{keyDisplay}:</span>
                      <span style={{ textAlign: 'left', width: '100px', flexShrink: 0 }}>{value.toLocaleString('he-IL', { maximumFractionDigits: 3 })}</span>
                      {!isAutoGenerated && (
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(item.id)} 
                          sx={{ color: 'red', padding: '4px' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* סה״כ הוצאות מס - Summary of all items - Only show when taxPercentage > 0 and not "חד צידית" */}
        {matchingReports.length > 0 && taxPercentage > 0 && !isSingleSidedAccountManagement && (
          <div style={{ marginTop: 10, marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: 8, textDecoration: 'underline' }}>
              <strong>{t('TotalTaxExpenses') || 'סה״כ הוצאות מס'}:</strong>
            </div>
            
            {/* הוצאות מס שוטפות */}
            <div style={{ fontSize: '14px', display: 'flex', direction: 'rtl', gap: 10, marginBottom: 5 }}>
              <span style={{ textAlign: 'right', minWidth: '180px', flexShrink: 0 }}>{t('CurrentTaxExpenses') || 'הוצאות מס שוטפות'}:</span>
              <span style={{ textAlign: 'left', width: '100px', flexShrink: 0 }}>{currentTaxExpenses.toLocaleString('he-IL', { maximumFractionDigits: 3 })}</span>
            </div>
            
            {/* הוצאות מס נדחות - Input field */}
            <div style={{ fontSize: '14px', display: 'flex', direction: 'rtl', alignItems: 'center', gap: 10, marginBottom: 5 }}>
              <span style={{ textAlign: 'right', minWidth: '180px', flexShrink: 0 }}>{t('DeferredTaxExpenses') || 'הוצאות מס נדחות'}:</span>
              <div style={{textAlign: 'left', width: '100px', flexShrink: 0 }}>
              <TextField
                size="small"
                type="number"
                placeholder="0"
                value={currentDeferredTaxExpensesInput}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setCurrentDeferredTaxExpensesInput(value);
                  // Don't update deferredTaxExpenses here to avoid triggering the useEffect
                  // It will be updated when saved
                  
                  // Update or create the deferred tax expenses entry
                  const existingItem = matchingReports.find(r => r.key === 'הוצאות מס נדחות' && r.type === 2);
                  if (existingItem) {
                    // Mark as modified if value changed
                    const originalValue = parseFloat(existingItem.value) || 0;
                    setMatchingReports(prev => prev.map(r => 
                      r.id === existingItem.id 
                        ? { ...r, value: value.toString(), isAmountPercentage: true, isModified: value !== originalValue }
                        : r
                    ));
                  } else if (value >= 0) {
                    // Create new entry (even if 0, so we can track it)
                    const newItem = {
                      id: `temp_deferred_tax_${Date.now()}`,
                      caseId: client?.caseId,
                      key: 'הוצאות מס נדחות',
                      isInternalKey: false,
                      internalKey: null,
                      type: 2, // TaxLiability
                      value: value.toString(),
                      isAmountPercentage: true,
                      year: normalizedYear,
                      isNew: true
                    };
                    setMatchingReports(prev => [...prev, newItem]);
                  }
                }}
                style={{ width: '100px', flexShrink: 0 }}
                InputProps={{
                  style: { paddingLeft: 0, paddingRight: 4, flexDirection: 'row-reverse', width: '100px' }
                }}
                inputProps={{
                  style: { padding: 1, textAlign: 'center'}
                }}
              />
              </div>
              {/* Show CheckIcon when value has changed */}
              {hasDeferredTaxExpensesChanged && (
                <IconButton
                  size="small"
                  onClick={handleSaveDeferredTaxExpensesLocal}
                  disabled={loading}
                  sx={{ color: '#304FFF', padding: '4px' }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              )}
            </div>
            
          </div>
        )}
        {/* סה״כ - Total */}
        {/* Show taxableIncomeBase if no tax liability items, otherwise show totalTaxExpenses */}
        {/* Use calculatedTotalTaxExpenses to reflect immediate changes in deferred tax expenses input */}
        <div style={{ fontSize: '14px', fontWeight: 600, display: 'flex', direction: 'rtl', gap: 10, marginTop: 8, paddingTop: 8, borderTop: '1px solid #ccc' }}>
          <span style={{ textAlign: 'right', minWidth: '180px', flexShrink: 0 }}><strong>{t('Total') || 'סה״כ'}:</strong></span>
          <span style={{ textAlign: 'left', width: '100px', flexShrink: 0 }}>
            <strong>
              {(() => {
                const totalValue = taxLiabilityItems.length === 0 
                  ? taxableIncomeBase 
                  : calculatedTotalTaxExpenses;
                return totalValue < 0 
                  ? `(${Math.abs(totalValue).toLocaleString('he-IL', { maximumFractionDigits: 2 })})`
                  : totalValue.toLocaleString('he-IL', { maximumFractionDigits: 2 });
              })()}
            </strong>
          </span>
        </div>
        {/* Taxable Income Calculation */}
        {/* <hr style={{ border: '1px solid #ccc', margin: '15px 0' }} />
        <div style={{ marginTop: 15, fontSize: '14px', fontWeight: 600, display: 'flex', direction: 'rtl', gap: 10 }}>
          <span style={{ textAlign: 'right', minWidth: '180px', flexShrink: 0 }}><strong>{t('TaxableIncome') || 'הכנסה חייבת'}:</strong></span>
          <span style={{ textAlign: 'left', width: '100px', flexShrink: 0 }}>{taxableIncomeBase.toLocaleString('he-IL', { maximumFractionDigits: 3 })}</span>
        </div> */}
      </div>
    </div>
  );
};

export default MatchingReportForm;